<template>
  <div class="templates-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <v-container class="templates-container">
      <!-- Hero Section -->
      <div class="templates-hero">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">Templates &amp; Resources</h1>
            <p class="hero-subtitle">Browse email templates and team resources for your outreach</p>
          </div>

          <div class="hero-controls">
            <div class="search-box">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input v-model="search" type="text" placeholder="Search..." />
            </div>

            <div v-if="activeTab === 0" class="filter-select">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2.5 5H17.5M5 10H15M7.5 15H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <select v-model="categoryFilter">
                <option value="All">All Categories</option>
                <option v-for="cat in categoriesFromData" :key="cat" :value="cat">{{ cat }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="content-tabs">
        <button
          v-for="(tab, idx) in tabs"
          :key="idx"
          :class="['tab-btn', { active: activeTab === idx }]"
          @click="activeTab = idx"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" v-html="tab.icon"></svg>
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- EMAIL TEMPLATES TAB -->
      <div v-if="activeTab === 0">
        <div class="templates-card">
          <div class="card-header">
            <div class="card-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Available Templates</span>
              <div class="template-count">{{ visibleTemplates.length }} templates</div>
            </div>
          </div>

          <!-- Templates Table -->
          <div v-if="!loading && visibleTemplates.length > 0" class="templates-table">
            <div class="table-header">
              <div class="th th-title">Title</div>
              <div class="th th-category">Category</div>
              <div class="th th-audience">Audience</div>
              <div class="th th-subjects">Subject Lines</div>
              <div class="th th-actions">Actions</div>
            </div>

            <div class="table-body">
              <div v-for="template in visibleTemplates" :key="template.id" class="template-row">
                <div class="td td-title">
                  <div class="template-info">
                    <div class="template-name">{{ template.title }}</div>
                  </div>
                </div>

                <div class="td td-category">
                  <span class="category-badge">{{ template.category }}</span>
                </div>

                <div class="td td-audience">
                  <span class="audience-text">{{ template.audience }}</span>
                </div>

                <div class="td td-subjects">
                  <div class="subject-list">
                    <div v-for="(subject, idx) in template.subjectLines.slice(0, 2)" :key="idx" class="subject-item">
                      • {{ subject }}
                    </div>
                    <div v-if="template.subjectLines.length > 2" class="subject-more">
                      +{{ template.subjectLines.length - 2 }} more
                    </div>
                  </div>
                </div>

                <div class="td td-actions">
                  <button class="btn-open" @click="openPreview(template)">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M1.5 9C1.5 9 3.75 3.75 9 3.75C14.25 3.75 16.5 9 16.5 9C16.5 9 14.25 14.25 9 14.25C3.75 14.25 1.5 9 1.5 9Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Open</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="loading" class="table-loading">
            <div class="loader"></div>
            <p>Loading templates...</p>
          </div>

          <!-- Empty State -->
          <div v-else class="table-empty">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <path d="M20 16H44M20 24H44M20 32H44M20 40H32" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <h3>No templates found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        </div>
      </div>

      <!-- TEAM RESOURCES TAB -->
      <div v-if="activeTab === 1">
        <div class="resources-card">
          <div class="card-header">
            <div class="card-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13 2V9H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Team Resources</span>
              <div class="template-count">{{ filteredResources.length }} files</div>
            </div>
          </div>

          <!-- Resources Grid -->
          <div v-if="!loadingResources && filteredResources.length > 0" class="resources-grid">
            <div
              v-for="resource in filteredResources"
              :key="resource.id"
              class="resource-card"
            >
              <div class="resource-preview">
                <img v-if="resource.mime.startsWith('image/')" :src="resource.url" alt="Resource" />
                <svg v-else width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <path :d="getFileIconPath(resource.mime)" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="resource-info">
                <div class="resource-name">{{ resource.name }}</div>
                <div class="resource-meta">
                  <span>{{ formatFileSize(resource.size) }}</span>
                  <span>{{ formatDate(resource.uploadedAt) }}</span>
                </div>
                <div class="resource-uploader">
                  Uploaded by {{ resource.uploadedByName || 'Admin' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="loadingResources" class="table-loading">
            <div class="loader"></div>
            <p>Loading resources...</p>
          </div>

          <!-- Empty State -->
          <div v-else class="table-empty">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <path d="M37.3333 5.33334H13.3333C11.9188 5.33334 10.5623 5.89524 9.5621 6.89543C8.56191 7.89562 8 9.25218 8 10.6667V53.3333C8 54.7478 8.56191 56.1044 9.5621 57.1046C10.5623 58.1048 11.9188 58.6667 13.3333 58.6667H50.6667C52.0812 58.6667 53.4377 58.1048 54.4379 57.1046C55.4381 56.1044 56 54.7478 56 53.3333V21.3333L37.3333 5.33334Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M37.3333 5.33334V21.3333H56" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3>No resources available</h3>
            <p>Check back later for team resources</p>
          </div>
        </div>
      </div>
    </v-container>

    <!-- Preview Modal -->
    <div v-if="preview.open" class="modal-overlay" @click="closePreview">
      <div class="modal-card modal-card-preview" @click.stop>
        <div class="modal-header">
          <h3>{{ preview.item?.title }}</h3>
          <button class="modal-close" @click="closePreview">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body" v-if="preview.item">
          <div class="template-meta">
            <span class="meta-badge">{{ preview.item.category }}</span>
            <span class="meta-divider">•</span>
            <span class="meta-text">{{ preview.item.audience }}</span>
          </div>

          <div class="preview-section">
            <div class="section-label">Subject Line Options</div>
            <div class="subject-chips">
              <button
                v-for="(subject, idx) in preview.item.subjectLines"
                :key="idx"
                class="subject-chip"
                @click="copyText(fill(subject))"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10.6667 5.33333V3.46667C10.6667 2.71993 10.6667 2.34656 10.5213 2.06135C10.3935 1.81046 10.1895 1.60649 9.93865 1.47866C9.65344 1.33333 9.28007 1.33333 8.53333 1.33333H3.46667C2.71993 1.33333 2.34656 1.33333 2.06135 1.47866C1.81046 1.60649 1.60649 1.81046 1.47866 2.06135C1.33333 2.34656 1.33333 2.71993 1.33333 3.46667V8.53333C1.33333 9.28007 1.33333 9.65344 1.47866 9.93865C1.60649 10.1895 1.81046 10.3935 2.06135 10.5213C2.34656 10.6667 2.71993 10.6667 3.46667 10.6667H5.33333M7.46667 14.6667H12.5333C13.2801 14.6667 13.6534 14.6667 13.9387 14.5213C14.1895 14.3935 14.3935 14.1895 14.5213 13.9387C14.6667 13.6534 14.6667 13.2801 14.6667 12.5333V7.46667C14.6667 6.71993 14.6667 6.34656 14.5213 6.06135C14.3935 5.81046 14.1895 5.60649 13.9387 5.47866C13.6534 5.33333 13.2801 5.33333 12.5333 5.33333H7.46667C6.71993 5.33333 6.34656 5.33333 6.06135 5.47866C5.81046 5.60649 5.60649 5.81046 5.47866 6.06135C5.33333 6.34656 5.33333 6.71993 5.33333 7.46667V12.5333C5.33333 13.2801 5.33333 13.6534 5.47866 13.9387C5.60649 14.1895 5.81046 14.3935 6.06135 14.5213C6.34656 14.6667 6.71993 14.6667 7.46667 14.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ fill(subject) }}
              </button>
            </div>
          </div>

          <div class="preview-section">
            <div class="section-label">Email Body</div>
            <div class="email-body-preview">
              <textarea :value="fill(preview.item.body)" readonly></textarea>
            </div>
          </div>

          <div class="preview-actions">
            <button class="btn-preview-action" @click="copyText(fill(preview.item.body))">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M12 6V4.2C12 3.0799 12 2.51984 11.782 2.09202C11.5903 1.71569 11.2843 1.40973 10.908 1.21799C10.4802 1 9.92011 1 8.8 1H4.2C3.0799 1 2.51984 1 2.09202 1.21799C1.71569 1.40973 1.40973 1.71569 1.21799 2.09202C1 2.51984 1 3.0799 1 4.2V8.8C1 9.92011 1 10.4802 1.21799 10.908C1.40973 11.2843 1.71569 11.5903 2.09202 11.782C2.51984 12 3.0799 12 4.2 12H6M9.8 17H14.2C15.3201 17 15.8802 17 16.308 16.782C16.6843 16.5903 16.9903 16.2843 17.182 15.908C17.4 15.4802 17.4 14.9201 17.4 13.8V9.4C17.4 8.27989 17.4 7.71984 17.182 7.29202C16.9903 6.91569 16.6843 6.60973 16.308 6.41799C15.8802 6.2 15.3201 6.2 14.2 6.2H9.8C8.67989 6.2 8.11984 6.2 7.69202 6.41799C7.31569 6.60973 7.00973 6.91569 6.81799 7.29202C6.6 7.71984 6.6 8.27989 6.6 9.4V13.8C6.6 14.9201 6.6 15.4802 6.81799 15.908C7.00973 16.2843 7.31569 16.5903 7.69202 16.782C8.11984 17 8.67989 17 9.8 17Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Copy Body
            </button>
            <button class="btn-preview-action btn-personalize" @click="openPersonalize(preview.item)">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M12.75 2.25L15.75 5.25M1.5 16.5L5.0625 16.0312C5.36831 15.9935 5.52121 15.9746 5.66458 15.9281C5.79263 15.887 5.91456 15.8304 6.02787 15.7598C6.15534 15.6802 6.26789 15.5777 6.493 15.3525L16.125 5.72054C16.9534 4.89211 16.9534 3.54789 16.125 2.71946C15.2966 1.89103 13.9524 1.89103 13.1239 2.71946L3.49196 12.3514C3.26685 12.5765 3.1543 12.6891 3.07467 12.8166C3.00407 12.9299 2.94749 13.0518 2.90646 13.1799C2.85989 13.3232 2.84098 13.4761 2.80316 13.782L1.5 16.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Personalize
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Personalize Modal -->
    <div v-if="dlg.open" class="modal-overlay" @click="closePersonalize">
      <div class="modal-card modal-card-large" @click.stop>
        <div class="modal-header">
          <h3>Personalize: {{ dlg.item?.title }}</h3>
          <button class="modal-close" @click="closePersonalize">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body personalize-body">
          <div class="personalize-grid">
            <div class="variables-section">
              <div class="section-label">Variables</div>
              <div class="variable-field">
                <label>General Manager's Name</label>
                <input v-model="vars.manager" type="text" placeholder="Jane Smith" />
              </div>
              <div class="variable-field">
                <label>Club Name</label>
                <input v-model="vars.club" type="text" placeholder="Fairview Country Club" />
              </div>
              <div class="variable-field">
                <label>Website Link</label>
                <input v-model="vars.website" type="text" placeholder="https://fairviewcc.com" />
              </div>
              <div class="variable-field">
                <label>Your Full Name</label>
                <input v-model="vars.yourName" type="text" placeholder="Jane Doe" />
              </div>
            </div>

            <div class="preview-section">
              <div class="section-label">Preview &amp; Copy</div>

              <div class="personalize-field">
                <label>Subject Line</label>
                <select v-model="dlg.selectedSubject" class="subject-dropdown">
                  <option v-for="(subject, idx) in (dlg.item?.subjectLines || [])" :key="idx" :value="fill(subject)">
                    {{ fill(subject) }}
                  </option>
                </select>
              </div>

              <div class="personalize-field">
                <label>Email Body</label>
                <textarea v-model="dlg.body" rows="12"></textarea>
              </div>

              <div class="personalize-actions">
                <button class="btn-copy" @click="copyText(dlg.selectedSubject)">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10.6667 5.33333V3.46667C10.6667 2.71993 10.6667 2.34656 10.5213 2.06135C10.3935 1.81046 10.1895 1.60649 9.93865 1.47866C9.65344 1.33333 9.28007 1.33333 8.53333 1.33333H3.46667C2.71993 1.33333 2.34656 1.33333 2.06135 1.47866C1.81046 1.60649 1.60649 1.81046 1.47866 2.06135C1.33333 2.34656 1.33333 2.71993 1.33333 3.46667V8.53333C1.33333 9.28007 1.33333 9.65344 1.47866 9.93865C1.60649 10.1895 1.81046 10.3935 2.06135 10.5213C2.34656 10.6667 2.71993 10.6667 3.46667 10.6667H5.33333M7.46667 14.6667H12.5333C13.2801 14.6667 13.6534 14.6667 13.9387 14.5213C14.1895 14.3935 14.3935 14.1895 14.5213 13.9387C14.6667 13.6534 14.6667 13.2801 14.6667 12.5333V7.46667C14.6667 6.71993 14.6667 6.34656 14.5213 6.06135C14.3935 5.81046 14.1895 5.60649 13.9387 5.47866C13.6534 5.33333 13.2801 5.33333 12.5333 5.33333H7.46667C6.71993 5.33333 6.34656 5.33333 6.06135 5.47866C5.81046 5.60649 5.60649 5.81046 5.47866 6.06135C5.33333 6.34656 5.33333 6.71993 5.33333 7.46667V12.5333C5.33333 13.2801 5.33333 13.6534 5.47866 13.9387C5.60649 14.1895 5.81046 14.3935 6.06135 14.5213C6.34656 14.6667 6.71993 14.6667 7.46667 14.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Copy Subject
                </button>
                <button class="btn-copy" @click="copyText(dlg.body)">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10.6667 5.33333V3.46667C10.6667 2.71993 10.6667 2.34656 10.5213 2.06135C10.3935 1.81046 10.1895 1.60649 9.93865 1.47866C9.65344 1.33333 9.28007 1.33333 8.53333 1.33333H3.46667C2.71993 1.33333 2.34656 1.33333 2.06135 1.47866C1.81046 1.60649 1.60649 1.81046 1.47866 2.06135C1.33333 2.34656 1.33333 2.71993 1.33333 3.46667V8.53333C1.33333 9.28007 1.33333 9.65344 1.47866 9.93865C1.60649 10.1895 1.81046 10.3935 2.06135 10.5213C2.34656 10.6667 2.71993 10.6667 3.46667 10.6667H5.33333M7.46667 14.6667H12.5333C13.2801 14.6667 13.6534 14.6667 13.9387 14.5213C14.1895 14.3935 14.3935 14.1895 14.5213 13.9387C14.6667 13.6534 14.6667 13.2801 14.6667 12.5333V7.46667C14.6667 6.71993 14.6667 6.34656 14.5213 6.06135C14.3935 5.81046 14.1895 5.60649 13.9387 5.47866C13.6534 5.33333 13.2801 5.33333 12.5333 5.33333H7.46667C6.71993 5.33333 6.34656 5.33333 6.06135 5.47866C5.81046 5.60649 5.60649 5.81046 5.47866 6.06135C5.33333 6.34656 5.33333 6.71993 5.33333 7.46667V12.5333C5.33333 13.2801 5.33333 13.6534 5.47866 13.9387C5.60649 14.1895 5.81046 14.3935 6.06135 14.5213C6.34656 14.6667 6.71993 14.6667 7.46667 14.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Copy Body
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Snackbar -->
    <div v-if="snack.show" class="snackbar">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { auth, db } from '../firebase'
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore'

export default {
  name: 'StaffTemplatesView',
  data() {
    return {
      activeTab: 0,
      tabs: [
        {
          label: 'Email Templates',
          icon: '<path d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.1087 15 17.5326 14.8244 17.8452 14.5118C18.1577 14.1993 18.3333 13.7754 18.3333 13.3333V6.66667C18.3333 6.22464 18.1577 5.80072 17.8452 5.48816C17.5326 5.17559 17.1087 5 16.6667 5H3.33333C2.89131 5 2.46738 5.17559 2.15482 5.48816C1.84226 5.80072 1.66667 6.22464 1.66667 6.66667V13.3333C1.66667 13.7754 1.84226 14.1993 2.15482 14.5118C2.46738 14.8244 2.89131 15 3.33333 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
        },
        {
          label: 'Team Resources',
          icon: '<path d="M13.3333 2H6.66667C5.93029 2 5.33333 2.59695 5.33333 3.33333V16.6667C5.33333 17.403 5.93029 18 6.66667 18H15.3333C16.0697 18 16.6667 17.403 16.6667 16.6667V5.33333L13.3333 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.3333 2V5.33333H16.6667M12.6667 10.6667H7.33333M12.6667 14H7.33333M9.33333 7.33333H7.33333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
        }
      ],

      user: null,
      search: '',
      categoryFilter: 'All',
      templates: [],
      resources: [],
      loading: true,
      loadingResources: true,
      snack: { show: false, msg: '' },
      _unsub: null,
      _unsubResources: null,
      vars: {
        manager: '',
        club: '',
        website: '',
        yourName: ''
      },
      dlg: {
        open: false,
        item: null,
        selectedSubject: '',
        body: ''
      },
      preview: {
        open: false,
        item: null
      }
    }
  },
  computed: {
    categoriesFromData() {
      const set = new Set(this.templates.map(t => t.category).filter(Boolean))
      return Array.from(set).sort()
    },
    visibleTemplates() {
      const q = this.search.toLowerCase().trim()
      const cat = this.categoryFilter
      return this.templates
        .filter(t => cat === 'All' || !cat || (t.category || '') === cat)
        .filter(t => !q || [t.title, t.category, t.audience].some(v => (v || '').toLowerCase().includes(q)))
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0))
    },
    filteredResources() {
      const q = this.search.toLowerCase().trim()
      return this.resources
        .filter(r => !q || (r.name || '').toLowerCase().includes(q))
        .sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0))
    }
  },
  mounted() {
    auth.onAuthStateChanged(u => {
      if (!u) return
      this.user = u
      this.listen()
      this.listenResources()
    })
  },
  beforeDestroy() {
    this._unsub && this._unsub()
    this._unsubResources && this._unsubResources()
  },
  methods: {
    listen() {
      this.loading = true
      const qRef = query(
        collection(db, 'templates'),
        where('published', '==', true)
      )
      this._unsub = onSnapshot(qRef, snap => {
        this.templates = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.loading = false
      }, (err) => {
        console.error('Templates listener error:', err)
        this.loading = false
        this.toast('Could not load templates')
      })
    },
    listenResources() {
      this.loadingResources = true
      const qRef = query(collection(db, 'teamResources'), orderBy('uploadedAt', 'desc'))
      this._unsubResources = onSnapshot(qRef, snap => {
        this.resources = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.loadingResources = false
      }, (err) => {
        console.error('Resources listener error:', err)
        this.loadingResources = false
      })
    },
    openPreview(item) {
      this.preview.item = item
      this.preview.open = true
    },
    closePreview() {
      this.preview.open = false
    },
    fill(text = '') {
      return (text || '')
        .replaceAll('[General Manager\'s Name]', this.vars.manager || '[General Manager\'s Name]')
        .replaceAll('[Club Name]', this.vars.club || '[Club Name]')
        .replaceAll('[Website Link]', this.vars.website || '[Website Link]')
        .replaceAll('[Your Full Name]', this.vars.yourName || '[Your Full Name]')
        .replaceAll('\r\n', '\n')
    },
    copyText(s) {
      navigator.clipboard.writeText(s || '')
        .then(() => {
          this.toast('✓ Copied to clipboard!')
        })
        .catch(() => {
          this.toast('✗ Could not copy')
        })
    },
    async copyImage(resource) {
      try {
        const response = await fetch(resource.url)
        const blob = await response.blob()
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ])
        this.toast('✓ Image copied to clipboard!')
      } catch (e) {
        console.error('Copy image error:', e)
        this.toast('✗ Could not copy image')
      }
    },
    openPersonalize(item) {
      this.preview.open = false
      this.dlg.item = item
      this.dlg.selectedSubject = (item?.subjectLines?.[0] ? this.fill(item.subjectLines[0]) : '')
      this.dlg.body = this.fill(item?.body || '')
      this.dlg.open = true
    },
    closePersonalize() {
      this.dlg.open = false
    },
    toast(msg) {
      this.snack = { show: true, msg }
      setTimeout(() => this.snack.show = false, 3000)
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatDate(ts) {
      if (!ts) return ''
      const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000)
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    getFileIconPath(mime) {
      if (mime.includes('pdf')) return 'M28 4H12C10.8954 4 10 4.89543 10 6V42C10 43.1046 10.8954 44 12 44H36C37.1046 44 38 43.1046 38 42V14L28 4ZM28 14V4L38 14H28Z'
      if (mime.includes('word') || mime.includes('document')) return 'M28 4H12C10.8954 4 10 4.89543 10 6V42C10 43.1046 10.8954 44 12 44H36C37.1046 44 38 43.1046 38 42V14L28 4ZM28 4V14H38M22 20H26M22 26H26M16 32H32M16 20H18M16 26H18'
      if (mime.includes('sheet') || mime.includes('excel')) return 'M38 6H10C8.89543 6 8 6.89543 8 8V40C8 41.1046 8.89543 42 10 42H38C39.1046 42 40 41.1046 40 40V8C40 6.89543 39.1046 6 38 6ZM24 12V36M12 24H36'
      if (mime.includes('presentation') || mime.includes('powerpoint')) return 'M36 6H12C10.8954 6 10 6.89543 10 8V40C10 41.1046 10.8954 42 12 42H36C37.1046 42 38 41.1046 38 40V8C38 6.89543 37.1046 6 36 6Z'
      if (mime.includes('video')) return 'M40 12L24 22L8 12M6 12L6 36C6 37.1046 6.89543 38 8 38H40C41.1046 38 42 37.1046 42 36V12C42 10.8954 41.1046 10 40 10H8C6.89543 10 6 10.8954 6 12Z'
      return 'M28 4H12C10.8954 4 10 4.89543 10 6V42C10 43.1046 10.8954 44 12 44H36C37.1046 44 38 43.1046 38 42V14L28 4Z'
    }
  }
}
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Montserrat:wght@400;500;600;700&display=swap');

  /* ==================== PAGE BACKGROUND ==================== */
  .templates-page {
    --bg-page: #16241d;
    --bg-alt: #1c3128;
    --bg-card: #203a2f;
    --bg-card-hover: #274637;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-gold: rgba(212, 175, 55, 0.35);
    --text-primary: #F5F6F4;
    --text-secondary: rgba(245, 246, 244, 0.68);
    --text-muted: rgba(245, 246, 244, 0.48);
    --gold: #d4af37;
    --gold-light: #e0c27a;
    --gold-dark: #a8862c;

    min-height: 100vh;
    background: linear-gradient(160deg, #1a2e26 0%, #2d4a3e 55%, #16241d 100%);
    position: relative;
    overflow-x: hidden;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
    padding: 40px 0 80px;
  }

  .templates-page .hero-title {
    font-family: 'Playfair Display', 'Georgia', serif;
  }

  .page-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(150px);
    opacity: 0.2;
    pointer-events: none;
    z-index: 0;
  }

  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.45), transparent);
    top: -200px;
    right: -200px;
  }

  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(91, 141, 191, 0.28), transparent);
    bottom: -150px;
    left: -150px;
  }

  .templates-container {
    position: relative;
    z-index: 2;
    max-width: 1400px;
    margin: 0 auto;
  }

  /* ==================== HERO ==================== */
  .templates-hero {
    margin-bottom: 24px;
  }

  .hero-content {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;
  }

  .hero-title {
    font-size: clamp(26px, 4vw, 34px);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .hero-subtitle {
    color: var(--text-secondary);
    font-size: 14.5px;
    margin: 0;
  }

  .hero-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-box,
  .filter-select {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-muted);
  }

  .search-box input,
  .filter-select select {
    background: transparent;
    border: none;
    outline: none;
    color: var(--text-primary);
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
  }

  .search-box input {
    width: 180px;
  }

  /* ==================== TABS ==================== */
  .content-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 24px;
  }

  .tab-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tab-btn.active {
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border-color: var(--gold);
    color: #1a2e26;
  }

  .tab-btn:not(.active):hover {
    border-color: var(--border-gold);
    color: var(--text-primary);
  }

  /* ==================== CARDS ==================== */
  .templates-card,
  .resources-card {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    padding: 26px;
  }

  .card-header {
    margin-bottom: 20px;
  }

  .card-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 18px;
    font-weight: 700;
  }

  .template-count {
    padding: 4px 12px;
    background: rgba(212, 175, 55, 0.1);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: var(--gold-light);
  }

  /* ==================== TEMPLATES TABLE ==================== */
  .templates-table {
    width: 100%;
  }

  .table-header {
    display: grid;
    grid-template-columns: 1.6fr 1fr 1fr 1.8fr 0.8fr;
    gap: 12px;
    padding: 0 14px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
  }

  .template-row {
    display: grid;
    grid-template-columns: 1.6fr 1fr 1fr 1.8fr 0.8fr;
    gap: 12px;
    align-items: center;
    padding: 16px 14px;
    border-radius: 12px;
    transition: all 0.2s ease;
  }

  .template-row:hover {
    background: var(--bg-alt);
  }

  .template-name {
    font-size: 14.5px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .category-badge {
    padding: 5px 12px;
    background: rgba(91, 141, 191, 0.12);
    border: 1px solid rgba(91, 141, 191, 0.3);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #8fb4dc;
  }

  .audience-text {
    font-size: 13px;
    color: var(--text-secondary);
  }

  .subject-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .subject-item {
    font-size: 12.5px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subject-more {
    font-size: 11.5px;
    color: var(--text-muted);
  }

  .btn-open {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-open:hover {
    border-color: var(--border-gold);
    color: var(--gold-light);
  }

  /* ==================== RESOURCES GRID ==================== */
  .resources-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 18px;
  }

  .resource-card {
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    overflow: hidden;
    transition: all 0.2s ease;
  }

  .resource-card:hover {
    border-color: var(--border-gold);
    transform: translateY(-4px);
  }

  .resource-preview {
    height: 120px;
    background: var(--bg-card-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
  }

  .resource-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .resource-info {
    padding: 14px;
  }

  .resource-name {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 6px;
  }

  .resource-meta {
    display: flex;
    gap: 10px;
    font-size: 11.5px;
    color: var(--text-muted);
    margin-bottom: 4px;
  }

  .resource-uploader {
    font-size: 11.5px;
    color: var(--text-muted);
  }

  /* ==================== LOADING / EMPTY ==================== */
  .table-loading,
  .table-empty {
    padding: 60px 24px;
    text-align: center;
    color: var(--text-muted);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .table-empty h3 {
    color: var(--text-primary);
    font-size: 18px;
  }

  .loader {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-subtle);
    border-top-color: var(--gold);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ==================== MODAL ==================== */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 24px;
  }

  .modal-card {
    background: var(--bg-card);
    border: 1px solid var(--border-gold);
    border-radius: 16px;
    max-width: 560px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-card-preview {
    max-width: 640px;
  }

  .modal-card-large {
    max-width: 780px;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 24px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .modal-header h3 {
    font-size: 16px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .modal-close {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
  }

  .modal-body {
    padding: 22px 24px;
  }

  .template-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }

  .meta-badge {
    padding: 4px 12px;
    background: rgba(91, 141, 191, 0.12);
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #8fb4dc;
  }

  .meta-divider {
    color: var(--text-muted);
  }

  .meta-text {
    font-size: 13px;
    color: var(--text-secondary);
  }

  .preview-section {
    margin-bottom: 20px;
  }

  .section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin-bottom: 10px;
  }

  .subject-chips {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .subject-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 9px;
    color: var(--text-secondary);
    font-size: 13.5px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .subject-chip:hover {
    border-color: var(--border-gold);
    color: var(--gold-light);
  }

  .email-body-preview textarea {
    width: 100%;
    min-height: 160px;
    padding: 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-secondary);
    font-family: 'Montserrat', sans-serif;
    font-size: 13.5px;
    line-height: 1.6;
    resize: vertical;
  }

  .preview-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .btn-preview-action {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 18px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 9px;
    color: var(--text-secondary);
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-preview-action:hover {
    border-color: var(--border-gold);
    color: var(--text-primary);
  }

  .btn-personalize {
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border: none;
    color: #1a2e26;
  }

  .btn-personalize:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
  }

  /* ==================== PERSONALIZE MODAL ==================== */
  .personalize-grid {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 24px;
  }

  .variable-field {
    margin-bottom: 16px;
  }

  .variable-field label {
    display: block;
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin-bottom: 6px;
  }

  .variable-field input,
  .personalize-field input,
  .personalize-field select,
  .personalize-field textarea {
    width: 100%;
    padding: 10px 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 9px;
    color: var(--text-primary);
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    outline: none;
  }

  .variable-field input:focus,
  .personalize-field select:focus,
  .personalize-field textarea:focus {
    border-color: var(--gold);
  }

  .personalize-field {
    margin-bottom: 16px;
  }

  .personalize-field label {
    display: block;
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin-bottom: 6px;
  }

  .personalize-actions {
    display: flex;
    gap: 10px;
  }

  .btn-copy {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 9px 14px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    color: var(--text-secondary);
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-copy:hover {
    border-color: var(--border-gold);
    color: var(--gold-light);
  }

  /* ==================== SNACKBAR ==================== */
  .snackbar {
    position: fixed;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 22px;
    background: #203a2f;
    border: 1px solid var(--border-gold);
    border-radius: 999px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.4);
    z-index: 3000;
  }

  .snackbar button {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
  }

  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 900px) {
    .table-header {
      display: none;
    }

    .template-row {
      grid-template-columns: 1fr;
      gap: 8px;
      background: var(--bg-alt);
      margin-bottom: 10px;
      border: 1px solid var(--border-subtle);
    }

    .personalize-grid {
      grid-template-columns: 1fr;
    }
  }
</style>